import { computed, reactive, ref, watch } from "vue";
import { eventBus, GlobalEvents } from "../global-events";
import { useCookieStorage, useSessionStorage } from "../use-storage";
import { useWecomaDatalayer } from "./use-wecoma-datalayer";
// eslint-disable-next-line no-restricted-imports
import Cookies from "js-cookie";
import { WecomaConstants } from "./wecoma-constants";
import { CookieBarMode, CookieType, WecomaCookie } from "./wecoma-models";
import { ContactWecomaKeys } from "../models/contact";
import { WECOMA_KEY_STORAGE } from "../log-login";

const cookieBarMode = ref<CookieBarMode>("initial");
const cookieBarModeExpiration = computed(() => {
    switch (cookieBarMode.value) {
        case "allRecheckExpired":
            return WecomaConstants.ALL_RECHECK_EXPIRATION;
        case "someRecheckExpired":
            return WecomaConstants.SOME_RECHECK_EXPIRATION;
        case "initial":
        default:
            return null;
    }
});

const consentsCached: Record<CookieType, boolean> = reactive(
    // iife generates initial value
    (() => {
        const data = Cookies.get(WecomaConstants.CONSENT_CACHE_COOKIE_NAME);

        return {
            essential: true,
            analytics: data?.[0] === "1",
            marketing: data?.[1] === "1",
            thirdpartycontent: data?.[2] === "1",
        };
    })()
);

const getWecomaCookieValue = (value: boolean) => (value ? "1" : "0");

watch(
    consentsCached,
    (newVal) => {
        if (newVal) {
            useCookieStorage().setItem(
                WecomaConstants.CONSENT_CACHE_COOKIE_NAME,
                getWecomaCookieValue(newVal.analytics) +
                    getWecomaCookieValue(newVal.marketing) +
                    getWecomaCookieValue(newVal.thirdpartycontent),
                "essential",
                {
                    expires: WecomaConstants.COOKIE_EXPIRATION,
                    sameSite: "strict",
                }
            );
        }
    },
    { deep: true }
);

const wecomaLoaded = new Promise((resolve) => {
    Wecoma.onready.push(resolve);
}).then(async () => {
    if (!environment.isKasa) {
        const storage = useSessionStorage();
        const keys = storage.getJson<ContactWecomaKeys>(WECOMA_KEY_STORAGE);
        if (!keys) {
            return;
        }

        const user = await _gdpr.wecoma.registerUser(keys.wecomaUserKey);
        if (!(await _gdpr.wecoma.userHasLocalManagedAuthKey())) {
            await user.registerManagedAuthKey(keys.wecomaDeviceKey);
        }
    }

    const [analyticsConsented, marketingConsented, thirdPartyConsented] = await Promise.all([
        _gdpr.wecoma.isConsented("analytics"),
        _gdpr.wecoma.isConsented("marketing"),
        _gdpr.wecoma.isConsented("thirdpartycontent"),
    ]);

    consentsCached.analytics = analyticsConsented;
    consentsCached.marketing = marketingConsented;
    consentsCached.thirdpartycontent = thirdPartyConsented;
});

export function useWecomaIntegration() {
    let currentCookieValue: WecomaCookie | null = null;

    const {
        pushViewCookieBarInitial,
        pushViewCookieBarRecheckAll,
        pushViewCookieBarRecheckSome,
        pushAllPurposesConsentClick,
        pushOnlyNecessaryPurposesConsent,
        pushRedirectToPreferenceCenter,
        pushReditectToPrivacyPolicy,
        pushThirdPartyConsent,
        pushLinksClickConsentBar,
    } = useWecomaDatalayer(cookieBarMode, cookieBarModeExpiration);

    const getNowTimestampInSeconds = () => Math.floor(Date.now() / 1000);

    const createWecomaCookieObject: () => WecomaCookie = () => {
        return {
            createdAt: getNowTimestampInSeconds(),
        };
    };

    const setWecomaCookie = () => {
        useCookieStorage().setItem(
            WecomaConstants.COOKIE_NAME,
            window.btoa(JSON.stringify(createWecomaCookieObject())),
            "essential",
            {
                domain: ".sazka.cz",
                expires: WecomaConstants.COOKIE_EXPIRATION,
            }
        );
    };

    const getWecomaCookieCreateTimestamp = () => {
        if (!currentCookieValue) {
            const val = useCookieStorage().getItem(WecomaConstants.COOKIE_NAME);
            if (val) {
                currentCookieValue = JSON.parse(window.atob(val));
            }
        }

        return currentCookieValue?.createdAt;
    };

    const wecomaCookieExists = () => {
        return !!getWecomaCookieCreateTimestamp();
    };

    const allPurposesConsented = async () => {
        await wecomaLoaded;
        return await _gdpr.wecoma.allPurposesConsented(_gdpr.wecoma.clientAuthKey, ["consentbar"]);
    };

    const allPurposesRevoked = async () => {
        await wecomaLoaded;
        return await _gdpr.wecoma.allPurposesRevoked(_gdpr.wecoma.clientAuthKey, ["consentbar"]);
    };

    const daysToSeconds = (d: number) => d * 24 * 60 * 60;

    const recheckAllExpired = () => {
        return (
            getWecomaCookieCreateTimestamp() <
            getNowTimestampInSeconds() - daysToSeconds(WecomaConstants.ALL_RECHECK_EXPIRATION)
        );
    };

    const recheckSomeExpired = () => {
        return (
            getWecomaCookieCreateTimestamp() <
            getNowTimestampInSeconds() - daysToSeconds(WecomaConstants.SOME_RECHECK_EXPIRATION)
        );
    };

    const shouldCookieBarOpen = async () => {
        if (environment.isInAppView) return false;

        if (!wecomaCookieExists()) {
            pushViewCookieBarInitial();
            return true;
        }

        if (await allPurposesConsented()) {
            return false;
        }

        if (await allPurposesRevoked()) {
            if (recheckAllExpired()) {
                cookieBarMode.value = "allRecheckExpired";
                pushViewCookieBarRecheckAll();
                return true;
            }
        } else {
            if (recheckSomeExpired()) {
                cookieBarMode.value = "someRecheckExpired";
                pushViewCookieBarRecheckSome();
                return true;
            }
        }

        return false;
    };

    const consentToAllPurposes = async () => {
        await wecomaLoaded;
        const purposes = await _gdpr.wecoma.retrievePurposes({
            contexts: ["consentbar"],
            authKeyTypes: [WecomaConstants.WECOMA_AUTHKEY],
        });

        for (const purpose of purposes) {
            purpose.consent();
        }

        pushAllPurposesConsentClick(purposes?.map((p) => p.code)?.join(","));

        setWecomaCookie();

        consentsCached.analytics = true;
        consentsCached.marketing = true;
        consentsCached.thirdpartycontent = true;

        eventBus.emit(GlobalEvents.ConsentsChanged);
    };

    const redirectToPreferenceCenter = (variant: "text" | "button") => {
        pushLinksClickConsentBar();
        pushRedirectToPreferenceCenter(variant);
    };

    const redirectToPrivacyPolicy = () => {
        pushLinksClickConsentBar();
        pushReditectToPrivacyPolicy();
    };

    const cookieTypeConsentedCached = (cookieType: CookieType): boolean => consentsCached[cookieType];

    const analyticsConsented = async (): Promise<boolean> => {
        await wecomaLoaded;
        return await _gdpr.wecoma.isConsented("analytics");
    };

    const marketingConsented = async (): Promise<boolean> => {
        await wecomaLoaded;
        return await _gdpr.wecoma.isConsented("marketing");
    };

    const thirdPartyConsented = async (): Promise<boolean> => {
        await wecomaLoaded;
        return await _gdpr.wecoma.isConsented("thirdpartycontent");
    };

    const consentToThirdParty = async () => {
        const purpose = await _gdpr.wecoma.retrievePurpose("thirdpartycontent");
        await purpose.consent();
        pushThirdPartyConsent();
        consentsCached.thirdpartycontent = true;
    };

    const consentToOnlyNecessaryPurposes = async () => {
        await wecomaLoaded;
        const purposes = await _gdpr.wecoma.retrievePurposes({
            contexts: ["consentbar"],
            authKeyTypes: [WecomaConstants.WECOMA_AUTHKEY],
        });

        for (const purpose of purposes) {
            purpose.revoke();
        }

        pushOnlyNecessaryPurposesConsent(purposes?.map((p) => p.code)?.join(","));

        setWecomaCookie();
        consentsCached.analytics = false;
        consentsCached.marketing = false;
        consentsCached.thirdpartycontent = false;
    };

    return {
        shouldCookieBarOpen,
        consentToAllPurposes,
        redirectToPreferenceCenter,
        redirectToPrivacyPolicy,
        cookieTypeConsentedCached,
        analyticsConsented,
        marketingConsented,
        thirdPartyConsented,
        consentToThirdParty,
        consentToOnlyNecessaryPurposes,
    };
}
